import * as React from "react";
import { Card } from "../../card";
import { DataTable } from "../data-table";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../dialog";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import { useApi, apiCall } from "../../api/client";
import { sql as sqlContract } from "../../api/contracts/database/sql";
import { snippets as snippetsContract, type SnippetDto } from "../../api/contracts/database/snippets";
import type { RunResultDto, StatementResultDto } from "../../api/contracts/database/sql";
import { WriteModeToggle } from "./write-mode-toggle";

const HISTORY_KEY = "db_explorer.history";
const HISTORY_MAX = 25;

function loadHistory(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function pushHistory(sql: string): string[] {
  const cur = loadHistory().filter((s) => s !== sql);
  const next = [sql, ...cur].slice(0, HISTORY_MAX);
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
  return next;
}

function toCsv(columns: string[], rows: unknown[][]): string {
  const esc = (v: unknown) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map(esc).join(",");
  const body = rows.map((r) => r.map(esc).join(",")).join("\n");
  return `${head}\n${body}\n`;
}

function download(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * SQL Editor — a monospace textarea over the single gated runner. Read-only by
 * default; the write-mode toggle flips it. Cmd/Ctrl-Enter runs. Results render
 * one tab per statement, errors in a clear card, with CSV/JSON export, saved
 * snippets (persisted in settings), and client-side history. PRD §5.3.
 */
export function SqlEditor({ initialSql = "" }: { initialSql?: string }) {
  const [text, setText] = React.useState(initialSql);
  const [writeMode, setWriteMode] = React.useState(false);
  const [result, setResult] = React.useState<RunResultDto | null>(null);
  const [running, setRunning] = React.useState(false);
  const [tab, setTab] = React.useState(0);
  const [history, setHistory] = React.useState<string[]>([]);
  const [showHistory, setShowHistory] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savingSnippet, setSavingSnippet] = React.useState(false);

  const { data: snippetData, mutate: mutateSnippets } = useApi(snippetsContract.list, {});
  const savedSnippets: SnippetDto[] = snippetData ?? [];

  React.useEffect(() => setHistory(loadHistory()), []);

  const run = React.useCallback(
    async (confirmDestructive = false) => {
      const sqlText = text.trim();
      if (!sqlText) return;
      setRunning(true);
      setError(null);
      try {
        const res = await apiCall(sqlContract.run, {
          body: { sql: sqlText, readOnly: !writeMode, confirmDestructive },
        });
        setResult(res);
        setTab(0);
        setHistory(pushHistory(sqlText));
      } catch (e) {
        setError(e instanceof Error ? e.message : "run failed");
      } finally {
        setRunning(false);
      }
    },
    [text, writeMode],
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      run();
    }
  }

  async function saveSnippet(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const next = [
      ...savedSnippets.filter((s) => s.name !== trimmed),
      { name: trimmed, sql: text },
    ];
    await apiCall(snippetsContract.save, { body: { snippets: next } });
    await mutateSnippets();
    setSavingSnippet(false);
  }

  const statements = result?.statements ?? [];
  const active: StatementResultDto | undefined = statements[tab];
  const needsConfirm =
    writeMode &&
    statements.some((s) => s.error?.includes("destructive operation requires confirmation"));

  return (
    <div className="flex flex-col gap-4">
      <WriteModeToggle writeMode={writeMode} onChange={setWriteMode} />

      {savedSnippets.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="type-subtext text-muted-foreground">Snippets:</span>
          {savedSnippets.map((s) => (
            <Button
              key={s.name}
              variant="outline"
              size="xs"
              onClick={() => setText(s.sql)}
            >
              {s.name}
            </Button>
          ))}
        </div>
      ) : null}

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        placeholder="SELECT name FROM sqlite_master LIMIT 5;"
        className="min-h-40 resize-y font-mono"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => run()} disabled={running || !text.trim()}>
          <Icon name="play" size="sm" />
          {running ? "Running…" : `Run${writeMode ? "" : " (read-only)"}`}
        </Button>
        {needsConfirm ? (
          <Button variant="destructive" onClick={() => run(true)} disabled={running}>
            <Icon name="triangle-alert" size="sm" /> Run anyway (destructive)
          </Button>
        ) : null}
        <span className="type-subtext text-muted-foreground">⌘/Ctrl+Enter</span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSavingSnippet(true)}
            disabled={!text.trim()}
          >
            <Icon name="bookmark" size="sm" /> Save snippet
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory((v) => !v)}
            disabled={history.length === 0}
          >
            <Icon name="history" size="sm" /> History
          </Button>
        </div>
      </div>

      {showHistory && history.length > 0 ? (
        <Card className="flex flex-col gap-px p-2">
          {history.slice(0, 12).map((h, i) => (
            <button
              key={i}
              onClick={() => {
                setText(h);
                setShowHistory(false);
              }}
              className="truncate rounded px-2 py-1 text-left font-mono type-subtext text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            >
              {h}
            </button>
          ))}
        </Card>
      ) : null}

      {error ? <p className="type-body text-destructive">{error}</p> : null}

      {statements.length > 0 ? (
        <div className="flex flex-col gap-2">
          {statements.length > 1 ? (
            <div className="flex flex-wrap gap-1">
              {statements.map((s, i) => (
                // button-ignore: segmented statement-tab selector, not an action button
                <button
                  key={i}
                  onClick={() => setTab(i)}
                  className={cn(
                    "rounded-md px-2.5 py-1 type-subtext",
                    i === tab
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.error ? "⚠ " : ""}#{i + 1} {s.kind}
                </button>
              ))}
            </div>
          ) : null}
          {active ? <StatementView stmt={active} /> : null}
        </div>
      ) : null}

      <SaveSnippetDialog
        open={savingSnippet}
        onClose={() => setSavingSnippet(false)}
        onSave={saveSnippet}
      />
    </div>
  );
}

function SaveSnippetDialog({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void | Promise<void>;
}) {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName("");
      setBusy(false);
    }
  }, [open]);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onSave(name);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save snippet</DialogTitle>
          <DialogDescription>
            Save the current query under a name. Re-saving an existing name
            overwrites it.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="Snippet name"
          autoFocus
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementView({ stmt }: { stmt: StatementResultDto }) {
  if (stmt.error) {
    return (
      <Card className="border border-destructive/40 bg-destructive/5 p-3">
        <p className="font-mono type-subtext text-destructive">{stmt.error}</p>
      </Card>
    );
  }
  if (stmt.kind !== "read") {
    return (
      <Card className="p-3">
        <p className="type-body text-muted-foreground">
          {stmt.changes ?? 0} row{(stmt.changes ?? 0) === 1 ? "" : "s"} affected.
        </p>
      </Card>
    );
  }
  const columns = stmt.columns ?? [];
  const rows = stmt.rows ?? [];
  return (
    <DataTable<unknown[]>
      title="Results"
      count={rows.length}
      strongHeader
      headerAction={
        <div className="flex items-center gap-1.5">
          {stmt.truncated ? <Badge variant="secondary">truncated</Badge> : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => download("query.csv", toCsv(columns, rows), "text/csv")}
            disabled={rows.length === 0}
          >
            CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              download(
                "query.json",
                JSON.stringify(
                  rows.map((r) => Object.fromEntries(columns.map((c, i) => [c, r[i]]))),
                  null,
                  2,
                ),
                "application/json",
              )
            }
            disabled={rows.length === 0}
          >
            JSON
          </Button>
        </div>
      }
      rows={rows}
      rowKey={(_r, i) => i}
      pageSize={50}
      empty="No rows."
      columns={columns.map((c, ci) => ({
        key: `${c}-${ci}`,
        header: <span className="font-mono">{c}</span>,
        cellClassName: "font-mono type-subtext",
        sort: (a: unknown[], b: unknown[]) =>
          String(a[ci] ?? "").localeCompare(String(b[ci] ?? ""), undefined, {
            numeric: true,
          }),
        render: (r: unknown[]) =>
          r[ci] === null || r[ci] === undefined ? (
            <span className="text-muted-foreground/50 italic">NULL</span>
          ) : (
            String(r[ci])
          ),
      }))}
    />
  );
}
